import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE = process.env.REACT_APP_SITE_URL || 'https://cajeta-hacienda-real.com';
const DEFAULT_OG_IMAGE = `${SITE}/images/og-cover.jpg`;

/**
 * Renders all per-page meta/OG/Twitter/JSON-LD tags via react-helmet-async.
 *
 * @param {string}  title       - Page-specific title (appended with site name)
 * @param {string}  description - Meta description (130–160 chars ideal)
 * @param {string}  canonical   - Path (e.g. "/mx/store"). Full URL is built from SITE.
 * @param {string}  image       - Absolute URL for OG image
 * @param {'es'|'en'} lang      - Page language (sets <html lang> and og:locale)
 * @param {string}  type        - og:type ("website" | "product" | "article")
 * @param {object|object[]} schema - JSON-LD schema object(s)
 * @param {boolean} noindex     - Suppress indexing (terms, privacy, admin pages)
 * @param {{es:string,en:string}} altLang - Alternate hreflang paths
 */
export function SEOHead({
  title,
  description,
  canonical,
  image,
  lang = 'es',
  type = 'website',
  schema,
  noindex = false,
  altLang,
}) {
  const fullTitle = title
    ? `${title} | Hacienda Real`
    : 'Hacienda Real – Cajeta Natural de Leche de Vaca';
  const ogImage = image || DEFAULT_OG_IMAGE;
  const canonicalUrl = canonical ? `${SITE}${canonical}` : null;
  const locale = lang === 'es' ? 'es_MX' : 'en_US';

  const schemas = schema ? (Array.isArray(schema) ? schema : [schema]) : [];

  return (
    <Helmet>
      <html lang={lang} />
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      {noindex
        ? <meta name="robots" content="noindex, nofollow" />
        : <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      }
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

      {/* hreflang for bilingual/biregional content */}
      {altLang?.es && <link rel="alternate" hreflang="es" href={`${SITE}${altLang.es}`} />}
      {altLang?.en && <link rel="alternate" hreflang="en" href={`${SITE}${altLang.en}`} />}
      {altLang?.es && <link rel="alternate" hreflang="x-default" href={`${SITE}${altLang.es}`} />}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:type" content={type} />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={fullTitle} />
      <meta property="og:site_name" content="Hacienda Real" />
      <meta property="og:locale" content={locale} />
      {altLang && <meta property="og:locale:alternate" content={lang === 'es' ? 'en_US' : 'es_MX'} />}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={fullTitle} />

      {/* JSON-LD structured data */}
      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
}
